import { Button, Form, Input, Message, Modal, Dropdown } from 'semantic-ui-react';
import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { AccountRead } from '../../../../store/generated/admin/account';
import {
  isCurrentUserStaffOrSuperAdmin,
  isCurrentUserSuperUser,
  isCurrentMemberOrgAdmin,
  ACCOUNT_ROLE,
} from '../../../../common/selectors/auth';

type Props = {
  open: boolean;
  initialAccount: AccountRead | null;
  onSave: (acc: AccountRead) => void;
  onClose: () => void;
  loading?: boolean;
  formError?: string;
};

export default function AddAccountModal({
  open, initialAccount, onSave, onClose, loading, formError,
}: Props) {
  const isAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isOrgAdmin = useSelector(isCurrentMemberOrgAdmin);
  const isSuperUser = useSelector(isCurrentUserSuperUser);

  const [account, setAccount] = useState<AccountRead>({
    id: 0, email: '', first_name: '', last_name: '', role: ACCOUNT_ROLE.STAFF, is_active: 'true', status: 'active',
  });

  const canManageAccounts = useMemo(() => isAdmin || isOrgAdmin, [isAdmin, isOrgAdmin]);

  useEffect(() => {
    if (!open) return;
    setAccount(
      initialAccount ?? {
        id: 0,
        email: '',
        first_name: '',
        last_name: '',
        role: ACCOUNT_ROLE.STAFF,
        is_active: 'true',
        status: 'active',
      } as AccountRead
    );
  }, [open, initialAccount]);

  const options = useMemo(() => {
    const base = [
      { key: 3, value: ACCOUNT_ROLE.REGULAR_USER, text: 'Regular User' },
      { key: 2, value: ACCOUNT_ROLE.STAFF, text: 'Staff' },
    ];
    if (isSuperUser || initialAccount?.role === ACCOUNT_ROLE.SUPERUSER) {
      base.unshift({ key: 1, value: ACCOUNT_ROLE.SUPERUSER, text: 'Admin' });
    }
    return base;
  }, [isSuperUser, initialAccount?.role]);

  const handleChange = (field: keyof AccountRead, value: any) =>
    setAccount(prev => ({ ...prev, [field]: value }));

  if (!canManageAccounts) {
    return (
      <Modal open={open} onClose={onClose} size="small">
        <Modal.Header>Unauthorized</Modal.Header>
        <Modal.Content>
          <Message negative>
            <Message.Header>Access Denied</Message.Header>
            <p>You do not have permission to add accounts.</p>
          </Message>
        </Modal.Content>
        <Modal.Actions><Button onClick={onClose}>Close</Button></Modal.Actions>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>{initialAccount ? 'Update Account' : 'Create Account'}</Modal.Header>
      <Modal.Content>
        {formError && <Message negative content={formError} />}
        <Form>
          <Form.Field>
            <label>First Name</label>
            <Input
              placeholder="Enter first name"
              value={account.first_name || ''}
              onChange={e => handleChange('first_name', e.target.value)}
            />
          </Form.Field>
          <Form.Field>
            <label>Last Name</label>
            <Input
              placeholder="Enter last name"
              value={account.last_name || ''}
              onChange={e => handleChange('last_name', e.target.value)}
            />
          </Form.Field>
          <Form.Field>
            <label>Email</label>
            <Input
              placeholder="Enter email address"
              value={account.email || ''}
              onChange={e => handleChange('email', e.target.value)}
            />
          </Form.Field>
          <Form.Field>
            <label>Account Type</label>
            <Dropdown
              placeholder="Select account type"
              fluid
              selection
              options={options}
              value={account.role}
              onChange={(_, data) => handleChange('role', data.value)}
              disabled={!isSuperUser && initialAccount?.role === ACCOUNT_ROLE.SUPERUSER}
            />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button positive onClick={() => onSave(account)} loading={loading}>
          {initialAccount ? 'Update Account' : 'Add Account'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
